import React from 'react';
import { styled, alpha } from '@mui/material/styles';
import Box from '@mui/material/Box';

/**
 * TimestampChip - A reusable component for displaying clickable timestamps
 * This component is designed to be used across the application for consistent styling of clickable timestamps.
 */

export interface TimestampChipProps {
  /**
   * Whether the timestamp is in a smaller/compact format
   */
  compact?: boolean;
  /**
   * The timestamp text to display
   */
  timestamp: string;
  /**
   * Handler for clicking on the timestamp
   */
  onClick?: () => void;
  /**
   * The sx prop passes styles to the component
   */
  sx?: any;
  /**
   * Optional className
   */
  className?: string;
  /**
   * Optional title tooltip for the timestamp
   */
  title?: string;
}

// Styled component for the timestamp
const StyledTimestampChip = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'compact',
})<{ compact?: boolean }>(({ theme, compact }) => ({
  cursor: 'pointer',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  color:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.9)
      : theme.palette.primary.main,
  fontSize: compact ? '0.75rem' : '0.8125rem',
  fontFamily: 'Inter',
  lineHeight: 1,
  padding: compact ? '2px 4px' : '4px 6px',
  margin: '0 2px',
  borderRadius: '4px',
  transition: 'all 0.15s ease',
  userSelect: 'none',
  backgroundColor:
    theme.palette.mode === 'dark'
      ? alpha(theme.palette.primary.main, 0.05)
      : alpha(theme.palette.primary.main, 0.04),
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'dark'
        ? alpha(theme.palette.primary.main, 0.1)
        : alpha(theme.palette.primary.main, 0.08),
  },
}));

/**
 * TimestampChip component
 *
 * @example
 * <TimestampChip timestamp="12:34" onClick={() => handleTimestampClick(754)} />
 */
const TimestampChip: React.FC<TimestampChipProps> = ({
  timestamp,
  onClick,
  compact = false,
  sx,
  className,
  title,
  ...props
}) => {
  return (
    <StyledTimestampChip
      onClick={onClick}
      component="span"
      compact={compact}
      sx={sx}
      className={className}
      title={title}
      {...props}
    >
      {timestamp}
    </StyledTimestampChip>
  );
};

export default TimestampChip;
